import { Link } from 'react-router';
import zigzagSvg from '@/assets/zigzag.svg';
import { DecorativeSvgMask } from '@/components/DecorativeSvgMask';
import type { HubEndCard } from '@/components/Hub/hubSectionTypes';
import { cn } from '@/lib/utils';
import 'material-symbols';

/** Wider than the card so the strokes run off every edge rather than sitting inside it as a motif. */
const ZIGZAG_SIZE = '140% auto';

// Inline because the `type-*` utilities are emitted after Tailwind's own, so a `font-bold` or
// `leading-*` class beside one is silently overridden.
const HEADING_STYLE = { fontWeight: 700 } as const;
const LABEL_STYLE = { lineHeight: '1.375rem' } as const;

export interface HubSectionEndCardProps {
    endCard: HubEndCard;
    /** The owning section's title, used for the analytics name. */
    sectionTitle: string;
}

// The accent ground is fixed across themes, so the text stays dark in both.
export const HubSectionEndCard = ({ endCard, sectionTitle }: HubSectionEndCardProps) => (
    <Link
        to={endCard.to}
        className={cn(
            // Fixed height so the card matches in both sections whatever sits beside it.
            'group relative flex h-[265px] flex-col justify-between gap-4 overflow-hidden rounded-xl p-6',
            endCard.groundClassName,
            'text-gz-neutral-900 transition hover:brightness-95',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gz-neutral-900',
        )}
        data-track-click={`Hub ${sectionTitle} End Card Clicked`}
        data-track-location="hub-panel"
        data-track-button-name="hub-end-card"
    >
        <DecorativeSvgMask
            src={zigzagSvg}
            maskFillClassName={endCard.zigzagClassName}
            opacityClassName={endCard.zigzagOpacityClassName}
            maskSize={ZIGZAG_SIZE}
        />
        <span className="relative z-10 flex flex-col gap-3">
            <span className="type-subheading-large" style={HEADING_STYLE}>{endCard.heading}</span>
            <span className="type-body-large" style={LABEL_STYLE}>{endCard.actionLabel}</span>
        </span>
        <span
            className="material-symbols-rounded relative z-10 self-end !text-[42px] transition-transform group-hover:translate-x-1"
            aria-hidden
        >
            arrow_forward
        </span>
    </Link>
);
